import {connectToStores} from 'epic-alt-utils';
import {Alert, Container, Section, Stack, Text} from 'epic-fortnite-react';
import HttpErrorScreen from 'epic-fortnite-react/components/error/HttpErrorScreen';
import {LoginStore, LoginStoreInterface} from 'epic-fortnite-react/stores';
import {useCmsData} from 'epic-fortnite-shared-values';
import queryString from 'query-string';
import React from 'react';
import styled from 'styled-components';
import root from 'window-or-global';

import CTAButton from '../components/common/CTAButton';
import LoadScreen from '../components/loadScreen/LoadScreen';

import LinkParagraph from './components/LinkParagraph';
import {SelectIsland} from './components/SelectIsland';
import {useSACStatus} from './submit-island.hooks';
import {SubmitIslandPageData} from './types';

interface Props {
  loginStore: LoginStoreInterface;
}

interface FunctionalViewComponent<Props> extends React.FC<Props> {
  getStores: () => any;
  getPropsFromStores: () => any;
}

const Styled = styled.div`
  margin: 64px 0;
`;

const slug = '/creative-mode/creative-submission-form';

const CreativeSubmissionFormView: FunctionalViewComponent<Props> = ({loginStore}) => {
  const {pageData: translations, isLoading, error} = useCmsData<SubmitIslandPageData>(slug);
  const {isActive = false, isActiveStaging = false} = translations;
  const {isLoggedIn, accountInfo} = loginStore;
  const {cabinedMode} = accountInfo;
  const {data: SACStatus} = useSACStatus(isLoggedIn);

  if (isLoading) {
    return <LoadScreen show={true} />;
  }

  if (error) {
    return <HttpErrorScreen statusCode={error.response?.status ?? 500} />;
  }

  if (!translations) {
    return <HttpErrorScreen statusCode={404} />;
  }

  const isInStagingEnvironment = Boolean(
    window.location.pathname.match(/fortnite-dissolve-fetching-heroics/)
  );

  let content: React.ReactNode = null;

  const {type = ''} = queryString.parse(location.search);

  if (!isLoggedIn) {
    const loginUrl = `${root.appContext || ''}/login?${queryString.stringify({
      state: root.location.pathname,
    })}`;
    root.location.href = loginUrl;
  } else if (
    (!isInStagingEnvironment && !isActive) ||
    (isInStagingEnvironment && !isActiveStaging)
  ) {
    content = <Alert tone="warning">{translations.submissionFormDisabledMessage}</Alert>;
  } else if (cabinedMode) {
    content = <Alert tone="warning">{translations.cabinedAccountMessage}</Alert>;
  } else if (SACStatus?.affiliated === false) {
    content = translations?.supportACreator?.noPublishingRights ? (
      <Alert tone="critical">
        <LinkParagraph linkDetails={translations?.supportACreator?.noPublishingRights} />
      </Alert>
    ) : (
      "Error: You're currently not a member of the Support-A-Creator 2.0 program."
    );
  } else if (SACStatus?.status !== 'ACTIVE') {
    content = translations?.supportACreator?.noPublishingRights ? (
      <Alert tone="critical">
        <LinkParagraph linkDetails={translations?.supportACreator?.noPublishingRights} />
      </Alert>
    ) : (
      "Error: You're currently not an active member of the Support-A-Creator 2.0 program."
    );
  } else if (SACStatus?.hasHyperWalletSetup === false) {
    content = translations?.supportACreator?.taxProfile ? (
      <Alert tone="critical">
        <LinkParagraph linkDetails={translations?.supportACreator.taxProfile} />
      </Alert>
    ) : (
      'Error: Please setup your Hyperwallet with the Support-A-Creator 2.0 program in order to proceed.'
    );
  } else {
    content = (
      <SelectIsland
        translations={translations}
        email={loginStore.accountInfo.email}
        submissionType={String(type)}
      />
    );
  }

  return (
    <Section backgroundColor="#fff" size="3xl" textAlign="center">
      <Container maxWidth={800} size="lg">
        <Stack gap="2xl">
          <Stack gap="lg">
            <Text color="#0b85ec" size="3xl" transform="uppercase">
              {translations?.header?.title}
            </Text>
            <Text size="lg" transform="uppercase">
              {translations?.header?.subtitle}
            </Text>
            {translations?.header?.contentGuidelines ? (
              <Text font="OpenSans" linkStyle="underline" size="med">
                <LinkParagraph linkDetails={translations?.header.contentGuidelines} />
              </Text>
            ) : undefined}
          </Stack>
        </Stack>
      </Container>
      {type && type !== '' ? (
        <>
          <Alert tone="warning">This submission type is no longer active</Alert>
          <CTAButton
            ctaConfig={{
              isCtaLinkExternal: false,
              ctaString: translations?.block20?.ctaReturn ?? 'See all islands',
              ctaLink: '/creative/creative-submission-form',
            }}
          />
        </>
      ) : (
        <Container maxWidth={800} size="lg">
          <Stack gap="2xl">
            <Styled>
              <Stack gap="lg">{content}</Stack>
            </Styled>
          </Stack>
        </Container>
      )}
    </Section>
  );
};

CreativeSubmissionFormView.getStores = () => {
  return [LoginStore];
};

CreativeSubmissionFormView.getPropsFromStores = (): Props => {
  return {
    loginStore: LoginStore.getState(),
  };
};

export default connectToStores(CreativeSubmissionFormView);
